import { useMutation, useQuery } from "@apollo/client/react";
import { gql } from "graphql-tag";
import Box from "@mui/material/Box";
import { captureException } from "@sentry/nextjs";
import { Disclaimer } from "@portal/app/_components/ui-elements/Disclaimer";
import {
  CreateUserAndEnrollmentForBasicInfoStepDocument,
  GetCustomerForBasicInfoStepDocument,
  GetProgramForBasicInfoStepDocument,
  UpdateCustomerForBasicInfoStepDocument,
} from "./generated";
import { useState } from "react";
import { styled } from "@mui/material/styles";
import { SignInInsteadDialog } from "@portal/app/login/_components/SignInInsteadDialog";
import { useAuthenticatedContext } from "../../context/AuthenticatedContext";
import { yupRules } from "@portal/app/_common/lib/yupValidationRules";
import { ClientFacingErrors } from "@portal/app/_common/types/clientErrors";
import { FormikDynamicField } from "@portal/app/_components/flow/DynamicFields";
import { AccountCreationFunnel } from "@portal/app/_common/types/customerDetail";
import { Formik } from "formik";
import { object as yupObject, string as yupString } from "yup";
import { detectKnownClientError } from "@portal/app/_common/lib/clientErrorUtils";
import { useFlowContext } from "@portal/app/_components/flow/FlowContext";
import { StepProps } from "../props";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { MixpanelEventName } from "@portal/app/_common/types/mixpanel";
import { useApolloCache } from "@portal/app/_components/apolloUtils";
import { ContactDisclaimer } from "../../ui-elements/ContactDisclaimer";
import { useSignUpAnalytics } from "@portal/app/_components/hooks/useSignUpAnalytics";
import { EMAIL_MISSPELLED_ERROR } from "@portal/app/_components/hooks/useEmailTypoWarning";
import { FlowForm, FlowFormSubmit, FlowFormSubmitWrapper } from "./components/FlowForm";
import { getNextCta } from "@portal/app/_common/lib/programHelpers";

void gql`
  query getCustomerForBasicInfoStep($customerId: ID!) {
    customer(where: { id: $customerId }) {
      id
      detailsMap {
        id
        acceptedDreamboundTerms
      }
      user {
        id
        nameFirst
        nameLast
        phone
        email
      }
    }
  }

  query getProgramForBasicInfoStep($programId: ID!) {
    program(where: { id: $programId }) {
      id
      provider {
        id
        isPartnered
        marketplaceConfig {
          id
          programFlowEmailStepHeader
          programFlowBasicInfoStepHeader
          programFlowHeadsUpPhoneDisclaimer
        }
      }
    }
  }

  mutation createUserAndEnrollmentForBasicInfoStep(
    $data: CustomerAndEnrollmentItemCreateInput!
    $programFlow: Boolean!
    $programId: ID
    $questionnaireFlow: Boolean!
    $skipperFlow: Boolean!
    $flowIntent: FlowIntent
    $signInFlow: Boolean!
  ) {
    createOneCustomer(data: $data) {
      customer {
        id
        user {
          id
          nameFirst
          nameLast
          phone
          email
          tokens {
            accessToken
            refreshToken
          }
        }
        ...FlowStateUpdateFragment
      }
    }
  }
  mutation updateCustomerForBasicInfoStep(
    $data: CustomerUpdateInput!
    $shouldGetTokens: Boolean!
    $programFlow: Boolean!
    $programId: ID
    $questionnaireFlow: Boolean!
    $skipperFlow: Boolean!
    $flowIntent: FlowIntent
    $signInFlow: Boolean!
  ) {
    updateViewerCustomer(data: $data) {
      id
      detailsMap {
        id
        acceptedDreamboundTerms
        acceptedTermsVersion
      }
      user {
        id
        nameFirst
        nameLast
        email
        phone
        tokens @include(if: $shouldGetTokens) {
          accessToken
          refreshToken
        }
      }
      ...FlowStateUpdateFragment
    }
  }
`;

export type BasicInfoStepValues = {
  nameFirst: string;
  nameLast: string;
  phone?: string | null;
  email: string;
  method: "FORM" | "GOOGLE";
};

const yupValidationSchemaShape = {
  nameFirst: yupRules.required.nameFirst,
  nameLast: yupRules.required.nameLast,
  email: yupRules.required.email,
  phone: yupRules.required.phone,
  verifiedPhoneToken: yupString(),
};

export const BasicInfoStep = ({
  formId,
  additionalCustomerDetails,
  mixpanelEventMap,
}: StepProps<{
  nameLast: MixpanelEventName;
  nameFirst: MixpanelEventName;
  email: MixpanelEventName;
  phone: MixpanelEventName;
  submitted: MixpanelEventName;
}> & {
  formId?: string;
  additionalCustomerDetails?: {
    coachStyle?: GQL.CoachStyle;
    coachThreadId?: string;
    accountCreationFunnel?: AccountCreationFunnel;
    fbPixelBrowserId?: string;
  };
}) => {
  const getSignUpAnalytics = useSignUpAnalytics();
  const {
    data: { programId },
    getFlowArgs,
    goForward,
  } = useFlowContext();
  const [signInInsteadDialogOpen, setSignInInsteadDialogOpen] = useState(false);
  const analytics = useMixpanel();
  const { evictRootQueries } = useApolloCache();
  const { openSignInModal, loggedIn, customerId } = useAuthenticatedContext();

  const { data } = useQuery(GetCustomerForBasicInfoStepDocument, {
    variables: { customerId: customerId! },
    skip: !customerId,
  });

  const { data: programData } = useQuery(GetProgramForBasicInfoStepDocument, {
    variables: { programId: programId! },
    skip: !programId,
  });

  const [createUserAndEnrollment] = useMutation(CreateUserAndEnrollmentForBasicInfoStepDocument, {
    onCompleted: () => {
      evictRootQueries([
        "getProgramEnrollmentItem",
        "currentEnrollmentItemsOfType",
        "resumableSchoolEnrollments",
      ]);
      return goForward();
    },
  });
  const [updateCustomer] = useMutation(UpdateCustomerForBasicInfoStepDocument, {
    onCompleted: () => {
      evictRootQueries([
        "resumableSchoolEnrollments",
        "currentEnrollmentItemsOfType",
        "getProgramEnrollmentItem",
      ]);
      return goForward();
    },
  });

  const initialValues: BasicInfoStepValues = {
    nameFirst: data?.customer?.user?.nameFirst ?? "",
    nameLast: data?.customer?.user?.nameLast ?? "",
    email: data?.customer?.user?.email ?? "",
    phone: data?.customer?.user?.phone ?? "",
    method: "FORM",
  };

  const onSubmit = async (values: BasicInfoStepValues) => {
    try {
      let mixpanelProperties;
      if (!loggedIn) {
        await createUserAndEnrollment({
          variables: {
            data: {
              nameFirst: values.nameFirst.trim(),
              nameLast: values.nameLast.trim(),
              email: values.email.toLowerCase().trim(),
              phone: values.phone || undefined,
              acceptedDreamboundTerms: true,
              ...(programId && programData?.program?.provider.isPartnered && { programId }),
              ...additionalCustomerDetails,
              ...getSignUpAnalytics(),
            },
            ...getFlowArgs(),
          },
        });
        mixpanelProperties = {
          operation: "CREATE",
        };
      } else {
        const email = values.email && !initialValues.email ? values.email.trim() : undefined;
        const phone = values.phone && !initialValues.phone ? values.phone.trim() : undefined;
        await updateCustomer({
          variables: {
            data: {
              nameFirst: values.nameFirst.trim(),
              nameLast: values.nameLast.trim(),
              ...(email && { email }),
              ...(phone && { phone }),
              ...additionalCustomerDetails,
              ...getSignUpAnalytics(),
            },
            shouldGetTokens: !!(email || phone),
            ...getFlowArgs(),
          },
        });
        mixpanelProperties = {
          operation: "UPDATE",
        };
      }
      return analytics.track(mixpanelEventMap.submitted, mixpanelProperties);
    } catch (error) {
      const clientError = detectKnownClientError(error);
      if (
        clientError === ClientFacingErrors.UserWithPhoneAlreadyExists ||
        clientError === ClientFacingErrors.UserWithEmailAlreadyExists
      ) {
        setSignInInsteadDialogOpen(true);
        return;
      }
      captureException(error);
    }
  };
  return (
    <>
      <Formik<BasicInfoStepValues>
        initialValues={initialValues}
        enableReinitialize
        validationSchema={yupObject().shape(yupValidationSchemaShape)}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, isValid, isSubmitting, errors }) => {
          const onlyTypoWarning =
            Object.keys(errors).length === 1 && errors.email === EMAIL_MISSPELLED_ERROR;
          return (
          <FlowForm id={formId} onSubmit={handleSubmit} data-testid="basic-info-step-form">
            <NameRow>
              <FormikDynamicField
                questionKey="NAME_FIRST"
                formikKey="nameFirst"
                loading={!data}
                formikFieldProps={{
                  onBlur: (event: React.FocusEvent<HTMLInputElement>) =>
                    analytics.track(mixpanelEventMap.nameFirst, {
                      value: event.target.value,
                    }),
                }}
              />
              <FormikDynamicField
                questionKey="NAME_LAST"
                formikKey="nameLast"
                loading={!data}
                formikFieldProps={{
                  onBlur: (event: React.FocusEvent<HTMLInputElement>) =>
                    analytics.track(mixpanelEventMap.nameLast, {
                      value: event.target.value,
                    }),
                }}
              />
            </NameRow>
            {!initialValues?.email && (
              <FormikDynamicField
                questionKey="EMAIL"
                formikKey="email"
                formikFieldProps={{
                  disabled: !!initialValues?.email,
                  onBlur: (event: React.FocusEvent<HTMLInputElement>) =>
                    analytics.track(mixpanelEventMap.email, {
                      value: event.target.value,
                    }),
                }}
                loading={!data}
              />
            )}
            <FormikDynamicField
              questionKey="PHONE"
              formikKey="phone"
              formikFieldProps={{
                disabled: !!initialValues?.phone,
                onBlur: (event: React.FocusEvent<HTMLInputElement>) =>
                  analytics.track(mixpanelEventMap.phone, {
                    value: event.target.value,
                  }),
              }}
              loading={!data}
            />
            <ContactDisclaimer
              type="PHONE"
              customText={
                programData?.program.provider.marketplaceConfig
                  ?.programFlowHeadsUpPhoneDisclaimer || undefined
              }
            />
            {!isValid && !onlyTypoWarning && (
              <Disclaimer style={{ marginTop: 0, marginBottom: "1rem" }}>
                {`You can't continue until everything is filled out.`}
              </Disclaimer>
            )}
            <FlowFormSubmitWrapper>
              <FlowFormSubmit disabled={(!isValid && !onlyTypoWarning) || isSubmitting}>
                {getNextCta()}
              </FlowFormSubmit>
            </FlowFormSubmitWrapper>
          </FlowForm>
          );
        }}
      </Formik>
      <SignInInsteadDialog
        onClose={() => {
          setSignInInsteadDialogOpen(false);
          openSignInModal("SIGN_IN");
        }}
        open={signInInsteadDialogOpen}
        isDreamSearch
      />
    </>
  );
};

const NameRow = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 1rem;
`;
